import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Filter, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface FilterOption<T = string> {
  value: T;
  label: string;
  color?: string;
}

interface FilterSelectProps<T = string> {
  options: FilterOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  popoverWidth?: string;
  /**
   * Renderiza o Popover em modo modal. Necessário quando o FilterSelect está
   * dentro de um Dialog (Radix), pois o react-remove-scroll do Dialog bloqueia
   * o scroll do conteúdo portalado do Popover. Em modo modal o Popover monta seu
   * próprio scroll-lock com escopo no conteúdo, liberando a rolagem da lista.
   */
  modal?: boolean;
}

export function FilterSelect<T extends string>({
  options,
  value,
  onChange,
  label = "Filtro",
  placeholder,
  className,
  popoverWidth = "w-[200px]",
  modal = true,
}: FilterSelectProps<T>) {
  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  return (
    <Popover modal={modal}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-8 border-dashed", className)}
        >
          <Filter className="mr-2 h-4 w-4" />
          {placeholder || label}
          <div className="mx-2 h-4 w-[1px] bg-gray-300" />
          <Badge
            variant="secondary"
            className={cn("rounded-sm px-1 font-normal", selectedOption?.color)}
          >
            {selectedOption?.label}
          </Badge>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("p-0", popoverWidth)} align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = value === option.value;
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => onChange(option.value)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <div
                        className={cn(
                          "flex h-4 w-4 items-center justify-center rounded-sm border",
                          isSelected
                            ? "bg-primary border-primary"
                            : "border-gray-300"
                        )}
                      >
                        {isSelected && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <span>{option.label}</span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
